import DocumentReadabilityContentSuggestion from "layout/documentReadabilityReport/DocumentReadabilityContentSuggestion";

export const strings = {
  "lix-readability-summary-stat": "How to interpret the LIX readability score?",
  "lix-readability-summary-stat-very-easy": "20 = Very easy to read",
  "lix-readability-summary-stat-easy": "between 20 and 30 = Easy to read",
  "lix-readability-summary-stat-somewhat":
    "between 30 and 45 = Somewhat difficult to read",
  "lix-readability-summary-stat-difficult":
    "between 45 and 60 = Difficult to read",
  "lix-readability-summary-stat-very-difficult": "60 = Very difficult to read",
  "flesch-tooltip":
    "Readability (Flesch) scores range from 0 to 100. The higher the better, above $1 is ideal.",
  "lix-tooltip":
    "Readability (LIX) scores range from 0 to 100. The lower the better, below $1 is ideal.",
  "title-difficult-to-read": "Difficult to read",
  "title-medium-to-read": "Somewhat difficult to read",
  "title-easy-to-read": "Easy to read",
  "score-difficult-to-read": "Your score is $1, making this difficult to read",
  "score-medium-to-read":
    "Your score is $1, making this somewhat difficult to read",
  "score-easy-to-read": "Your score is $1, making this easy to read",
  "grade-easy": "Your reader needs only $1 years of education.",
  "grade-medium": "Your reader needs $1 years of education.",
};

export const issueClasses = {
  Passive: "passive",
  HiddenVerb: "hidden_verb",
  Sentence: "long_sentence",
  VeryLongSentence: "very_long_sentence",
  LongWord: "long_word",
};

export const highlightIssueRegions = (text, highlights) => {
  // Collect all split points
  const points = new Set([0, text.length]);
  highlights.forEach(({ start, end }) => {
    points.add(start);
    points.add(end);
  });
  // Sort point positions
  const sortedPoints = Array.from(points).sort((a, b) => a - b);
  // Derive fragments (list of elements to display with highlights of issues)
  const fragments = [];
  for (let i = 0; i < sortedPoints.length - 1; i++) {
    const start = sortedPoints[i];
    const end = sortedPoints[i + 1];
    const substr = text.slice(start, end);
    const activeHighlights = highlights.filter(
      (h) => h.start < end && h.end > start
    );
    // Get maximum contained highlight i.e. active highlight with greatest start position
    let mostContained = null;
    if (activeHighlights.length) {
      mostContained = activeHighlights.reduce((max, highlight) => {
        return highlight.start > max.start ? highlight : max;
      });
    }
    // Add fragment
    if (mostContained) {
      let containerCls = "";
      if (mostContained.title === null && activeHighlights.length > 1) {
        const containerHighlights = activeHighlights.filter((a) => a.title);
        if (containerHighlights.length > 0) {
          containerCls = `highlight_${issueClasses[activeHighlights.filter((a) => a.title).reverse()[0].issue.issueType]}`;
        }
      }
      fragments.push(
        <DocumentReadabilityContentSuggestion
          issueHit={mostContained}
          content={substr}
          issueCls={`${issueClasses[mostContained.issue.issueType]} ${containerCls}`}
        />
      );
    } else {
      const span = <span>{substr}</span>;
      fragments.push(span);
    }
  }
  return fragments;
};

export const isEnglish = (documentInfo) =>
  documentInfo.languageDetected === "NA" ||
  documentInfo.languageDetected === "EN";

export const getReadabilityData = (documentInfo, summary, rowData) => {
  let score = "";
  let colour = "yellow";
  let scoreType = "medium";
  let calloutTitle = strings["title-medium-to-read"];
  let calloutBody = "";
  if (rowData.stats.analysis?.sentenceStatistics !== undefined) {
    if (isEnglish(documentInfo)) {
      score = parseInt(
        rowData.stats.analysis.sentenceStatistics.fleschReadingLevel
      );
      calloutBody = strings["flesch-tooltip"].replace("$1", score);
      if (score >= summary.thresholdSettings.thresholdReadabilityUpper) {
        colour = "green";
        scoreType = "good";
        calloutTitle = strings["title-easy-to-read"];
      }
      if (score <= summary.thresholdSettings.thresholdReadabilityLower) {
        colour = "red";
        scoreType = "bad";
        calloutTitle = strings["title-difficult-to-read"];
      }
    } else {
      score = parseInt(
        rowData.stats.analysis.sentenceStatistics.lixReadability
      );
      calloutBody = strings["lix-tooltip"].replace("$1", score);
      if (score <= summary.thresholdSettings.thresholdLixReadabilityLower) {
        colour = "green";
        scoreType = "good";
        calloutTitle = strings["title-easy-to-read"];
      }
      if (score >= summary.thresholdSettings.thresholdLixReadabilityUpper) {
        colour = "red";
        scoreType = "bad";
        calloutTitle = strings["title-difficult-to-read"];
      }
    }
  }
  return {
    score,
    colour,
    calloutBody,
    calloutTitle,
    scoreType,
  };
};

export const getGradeLevel = (documentInfo, summary, rowData) => {
  let colour = "yellow";
  let scoreType = "medium";
  const score = isEnglish(documentInfo)
    ? rowData.stats.analysis?.sentenceStatistics?.fleschKincaidReadingLevel?.toFixed(
        1
      )
    : rowData.stats.analysis?.sentenceStatistics?.lixGradeLevel?.toFixed(1);
  let calloutTitle = strings["title-medium-to-read"];
  let calloutBody = strings["grade-medium"].replace("$1", score);
  if (score <= summary.thresholdSettings.thresholdGradeLevelLower) {
    colour = "green";
    scoreType = "good";
    calloutTitle = strings["title-easy-to-read"];
    calloutBody = strings["grade-easy"].replace("$1", score);
  }
  if (score >= summary.thresholdSettings.thresholdGradeLevelUpper) {
    colour = "red";
    scoreType = "bad";
    calloutTitle = strings["title-difficult-to-read"];
  }
  return {
    score,
    colour,
    calloutBody,
    calloutTitle,
    scoreType,
  };
};

export const getSentencesScoreInfo = ({ summary, analysis }) => {
  let summaryLine = "";
  let calloutBody = "";
  const longSentenceDensity = (
    (100 * analysis.sentenceStatistics.longSentenceCount) /
    analysis.sentenceStatistics.count
  ).toFixed(2);
  const score = `${longSentenceDensity}%`;
  let scoreType = "medium";
  if (
    longSentenceDensity <= summary.thresholdSettings.thresholdLongSentenceLower
  ) {
    scoreType = "good";
  } else if (
    longSentenceDensity >= summary.thresholdSettings.thresholdLongSentenceUpper
  ) {
    scoreType = "bad";
  }
  calloutBody = (
    <span>
      Long sentences are greater than{" "}
      {summary.thresholdSettings.longSentenceCutoff} words. Long sentences often
      mask multiple concepts.
      <div style={{ paddingTop: "8px" }}></div>
      Split your long sentences to make things simpler. Consider using bullet
      points or lists.
    </span>
  );
  if (analysis.sentenceStatistics.longSentenceCount > 0) {
    summaryLine = `${analysis.sentenceStatistics.longSentenceCount} sentence${analysis.sentenceStatistics.longSentenceCount > 1 ? "s" : ""}`;
  }
  return {
    scoreType,
    title: "Long Sentences",
    score,
    summaryLine,
    calloutTitle: <b>Long Sentences</b>,
    calloutBody,
  };
};

export const getPassiveVoiceScoreInfo = ({
  summary,
  analysis,
  documentInfo,
}) => {
  let scoreType = "neutral";
  let score = "---";
  let summaryLine = "";
  let calloutBody = (
    <span>Passive voice does not apply with Non-English documents.</span>
  ); // i18n this?
  if (isEnglish(documentInfo)) {
    const passiveSentenceDensity = (
      (100 * analysis.sentenceStatistics.passiveSentenceCount) /
      analysis.sentenceStatistics.count
    ).toFixed(2);
    score = `${passiveSentenceDensity}%`;
    scoreType = "medium";
    if (
      passiveSentenceDensity <=
      summary.thresholdSettings.thresholdPassiveVoiceLower
    ) {
      scoreType = "good";
    } else if (
      passiveSentenceDensity >=
      summary.thresholdSettings.thresholdPassiveVoiceUpper
    ) {
      scoreType = "bad";
    }
    calloutBody = (
      <span>
        Passive voice is where the subject of the verb is the recipient (not the
        source) of the action.
        <div style={{ paddingTop: "8px" }}></div>
        &ldquo;Quality is monitored&rdquo; vs. &ldquo;We monitor quality&rdquo;.
        <div style={{ paddingTop: "8px" }}></div>
        If you use &ldquo;active&rdquo; voice, you will get better clarity and
        strength.
      </span>
    );
    if (analysis.sentenceStatistics.passiveSentenceCount > 0) {
      summaryLine = `${analysis.sentenceStatistics.passiveSentenceCount} sentence${analysis.sentenceStatistics.passiveSentenceCount > 0 ? "s" : ""}`;
    }
  }
  return {
    scoreType,
    title: "Passive Voice",
    score,
    summaryLine,
    calloutTitle: <b>Passive Voice</b>,
    calloutBody,
  };
};

export const getReadabilityScoreInfo = ({
  summary,
  analysis,
  documentInfo,
}) => {
  let scoreType = "medium";
  let title = "Readability (LIX)";
  let calloutBody = (
    <div>
      <p>
        <b>Readability (LIX)</b>
      </p>
      <span>
        {strings["lix-readability-summary-stat"]}
        <div style={{ paddingTop: "8px" }}></div>
        <ul className="lixHoverlist">
          <li>{strings["lix-readability-summary-stat-very-easy"]}</li>
          <li>{strings["lix-readability-summary-stat-easy"]}</li>
          <li>{strings["lix-readability-summary-stat-somewhat"]}</li>
          <li>{strings["lix-readability-summary-stat-difficult"]}</li>
          <li>{strings["lix-readability-summary-stat-very-difficult"]}</li>
        </ul>
      </span>
    </div>
  );
  let score;
  if (isEnglish(documentInfo)) {
    const s = analysis.sentenceStatistics.fleschReadingLevel;
    score = (
      <>
        {parseInt(s)}
        <span style={{ fontSize: "10px" }}> / 100</span>
      </>
    );
    title = "Readability (Flesch)";
    calloutBody = (
      <span>
        Readability ranges from 0 to 100. 100 is the top mark. Look to achieve
        50 or above.<div style={{ paddingTop: "8px" }}></div>This is based on
        the Flesch Reading Ease index.
      </span>
    );
    if (s >= summary.thresholdSettings.thresholdReadabilityUpper) {
      scoreType = "good";
    }
    if (s <= summary.thresholdSettings.thresholdReadabilityLower) {
      scoreType = "bad";
    }
  } else {
    const s = parseInt(analysis.sentenceStatistics.lixReadability);
    score = `${s}`;
    if (s <= summary.thresholdSettings.thresholdReadabilityLower) {
      scoreType = "good";
    }
    if (s >= summary.thresholdSettings.thresholdReadabilityUpper) {
      scoreType = "bad";
    }
  }
  return {
    scoreType,
    title,
    score,
    summaryLine: "",
    calloutTitle: <b>{title}</b>,
    calloutBody,
  };
};

export const getGradeScoreInfo = ({ summary, analysis, documentInfo }) => {
  let score = "-.-";
  let scoreType = "neutral";
  if (summary) {
    let s = analysis.sentenceStatistics.lixGradeLevel;
    scoreType = "medium";
    if (isEnglish(documentInfo)) {
      s = analysis.sentenceStatistics.fleschKincaidReadingLevel;
    }
    score = s.toFixed(1);
    if (s <= summary.thresholdSettings.thresholdGradeLevelLower) {
      scoreType = "good";
    }
    if (s >= summary.thresholdSettings.thresholdGradeLevelUpper) {
      scoreType = "bad";
    }
  }
  return {
    scoreType,
    title: "Grade Level",
    score,
    summaryLine: "",
    calloutTitle: <b>Grade Level</b>,
    calloutBody: (
      <span>
        Represents number of years of education generally required to understand
        this text.
        <div style={{ paddingTop: "8px" }}></div>
        Use this score to determine if your content is too complex for your
        audience. The average reading grade level among US adults is Grade 8.
      </span>
    ),
  };
};

export const getScoreInfo = (summary, analysis, documentInfo, type) => {
  switch (type) {
    case "sentences":
      return getSentencesScoreInfo({ summary, analysis, documentInfo });
    case "passive-voice":
      return getPassiveVoiceScoreInfo({ summary, analysis, documentInfo });
    case "readability":
      return getReadabilityScoreInfo({ summary, analysis, documentInfo });
    case "grade":
    default:
      return getGradeScoreInfo({ summary, analysis, documentInfo });
  }
};
